import React, { useCallback, useEffect, useState } from "react";
import { FilesBlock } from "./files-block";
import { Check, ExclamationCircle } from "react-bootstrap-icons";
import { Button, Dropdown } from "react-bootstrap";
import { ChatMessageItemProps } from "./message-item.props";
import { MessageStatusIndicator } from "./message-status-indicator";
import { getFileType, getUserNameForChat, MediaItem } from "@/shared/lib";
import { useMediaModal } from "@/shared/hooks";
import "App.css";
import { useAuthStore } from "@/features/auth";
import { ReplyPreview } from "./reply-preview";
import { MediaModal, renderWithLinks } from "@/shared/ui";
import { useScrollStore } from "@/features/chat/model";

export const ChatMessageItem = React.memo(
  ({
    ref,
    msg,
    allFiles,
    isUserMsg,
    loadingMessage,
    onDoubleClick,
    onClickMessegeReply,
    onEditMessage,
    onDeleteMessage,
    modeSelected,
    isSelected,
    handleRightClick,
    onResend,
    goToMessage,
  }: ChatMessageItemProps) => {
    const hasText = !!msg.text?.trim();
    const hasFilesUrls = (msg?.fileUrls?.length || 0) > 0;
    const hasFiles = (msg?.files?.length || 0) > 0;
    
    const [isFileBlockHovered, setIsFileBlockHovered] = useState(false);
    const [forwardedFromName, setForwardedFromName] = useState<string>("User");
    const [replyName, setReplyName] = useState<string>("User");

    useEffect(() => {
      if (!msg.forwardedFrom) return;

      getUserNameForChat(msg.forwardedFrom).then((name) => {
        setForwardedFromName(name || "User");
      });
    }, [msg.forwardedFrom]);

    useEffect(() => {
      if (!msg.reply) return;

      const chatId = typeof msg.reply.chat === "string" ? msg.reply.chat : msg.reply.chat._id 
      const ownerId = typeof msg.reply.owner === "string" ? msg.reply.owner : msg.reply.owner._id
      getUserNameForChat(chatId, ownerId).then((name) => {
        setReplyName(name || "User");
      });
    }, [msg.reply]);

    const isVoiceMessage = (): boolean => {
      if (msg.files?.length === 1) {
        const file = msg.files[0];
        return file instanceof File && file.type.startsWith("audio/");
      }
      if (msg.fileUrls?.length === 1) {
        return /\.(wav|ogg|mp3|m4a)$/i.test(msg.fileUrls[0]);
      }
      return false;
    };

    const {
      modalMedia,
      currentIndex,
      openMediaModal,
      closeMediaModal,
      handlePrev,
      handleNext,
      isOpen,
    } = useMediaModal();

    const openFileModal = (url: string, msgId?: string) => {
      if (modeSelected) return;
      
      const mediaItems: MediaItem[] = allFiles
        .map((file) => {
          if (file.url.startsWith("blob:")) {
            const type = getFileType(file.url) as "image" | "video" | string;
            return {
              url: file.url,
              type: type === "image" || type === "video" || type === "text" ? type : "other",
              msgId: file.msgId
            } as MediaItem;
          } else {
            const type = getFileType(file.url) as "image" | "video" | string;
            return { url: file.url, type, msgId: file.msgId } as MediaItem;
          }
        })
        .filter(
          (item) =>
            item.url.startsWith("blob:") ||
            item.type === "image" ||
            item.type === "video"
        );

      const index = mediaItems.findIndex(
        (item) => item.url === url && item.msgId === msgId
      );


      if (index === -1) {
        console.warn(`File is not an image or video, or not found:`, url);
        return;
      }

      openMediaModal(mediaItems, index);
    };

    if (!hasText && !hasFilesUrls && !hasFiles) return null;

    const hasOtherFiles = msg?.files?.find((file) => getFileType(file) === "other") !== undefined
    //  || msg?.fileUrls?.find((file) => getFileType(file) === "other") !== undefined;
    
    return (
      <>
        <div
          key={msg._id}
          className={`d-flex align-items-end gap-2 ${isUserMsg
            ? "justify-content-end"
            : modeSelected
              ? "justify-content-between"
              : "justify-content-start"
            } mb-2`}
          style={{ userSelect: modeSelected ? "none" : "text" }}
          onDoubleClick={onDoubleClick}
        >
          <div
            className={
              hasText
                ? (hasFiles || hasFilesUrls) && !hasOtherFiles
                  ? msg.forwardedFrom ? "rounded-4" : "rounded-top-5 rounded-bottom-4"
                  : "rounded-4"
                : msg.forwardedFrom ? "rounded-top-4 rounded-bottom-5" : hasOtherFiles ? "rounded-4" : "rounded-5"
            }
            ref={ref}
            onContextMenu={(e) => {
              e.preventDefault();              
              const native = e.nativeEvent as PointerEvent;

              if (native.pointerType !== "touch") {
                handleRightClick?.(e as any, msg);
              }
            }}
            onClick={(e: React.MouseEvent) => {
              const native = e.nativeEvent as PointerEvent;
              if (native.pointerType === "touch") {
                handleRightClick?.(e as any, msg);
              }
            }}
            style={{
              maxWidth: "75%",
              background: isSelected
                ? "#1c77ffe6"
                : isUserMsg
                  ? "#f8f9fa"
                  : "#6c757d",
              color: isSelected ? "#fff" : isUserMsg ? "#000" : "#fff",
              padding:
                (hasFiles || hasFilesUrls) && !hasOtherFiles ? "0px" :
                  hasOtherFiles ? "4px" : "4px 8px 5px 8px",
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
              display: "inline-block",
              position: "relative",
            }}
          >
            {msg.forwardedFrom && (
              <span className="text-muted fw-bold" style={{ fontSize: "12px", marginLeft: (hasFiles || hasFilesUrls) ? "8px" : 0 }} >
                Переслано от {forwardedFromName}
              </span>
            )}

            {msg.reply && (
              <ReplyPreview
                isSelected={isSelected}
                msg={msg.reply}
                messageItemRef={ref}
                isUserMsg={isUserMsg}
                onClickMessegeReply={onClickMessegeReply}
                name={replyName}
              />
            )}

            {(hasFiles || hasFilesUrls) && (
              <div
                onTouchStart={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
                onTouchEnd={(e) => e.stopPropagation()}
                style={{ position: "relative", width: "100%" }}
                onMouseEnter={() => {
                  if (!isVoiceMessage()) {
                    setIsFileBlockHovered(true);
                  }
                }}
                onDoubleClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onMouseLeave={() => {
                  if (!isVoiceMessage()) {
                    setIsFileBlockHovered(false);
                  }
                }}
              >
                {hasFiles ? (
                  <div>
                    <FilesBlock
                      isSelected={isSelected}
                      filesFromUser={isUserMsg}
                      filesArr={msg.files || []}
                      fileUrls={msg.fileUrls || []}
                      filesErr={msg.errFiles || []}
                      loadingMessage={loadingMessage}
                      handleRightClick={(e, type, file) => {
                        handleRightClick(e, msg, type, file);
                      }}
                      openFileModal={(url) => openFileModal(url, msg._id)}
                      msgErr={!!msg.err}
                      hasText={hasText}
                      hasTextTop={!!msg.forwardedFrom}
                    />
                  </div>
                ) : (
                  <FilesBlock
                    isSelected={isSelected}
                    filesFromUser={isUserMsg}
                    filesArr={msg.fileUrls || []}
                    fileUrls={msg.fileUrls || []}
                    filesErr={msg.errFiles || []}
                    loadingMessage={loadingMessage}
                    handleRightClick={(e, type, file) => {
                      handleRightClick(e, msg, type, file);
                    }}
                    openFileModal={(url) => openFileModal(url, msg._id)}
                    msgErr={!!msg.err}
                    hasText={hasText}
                    hasTextTop={!!msg.forwardedFrom}
                  />
                )}

                {(isVoiceMessage() || isFileBlockHovered) &&
                  !hasText &&
                  !loadingMessage && (
                    <MessageStatusIndicator
                      msg={msg}
                      isUserMsg={isUserMsg}
                      isSelected={isSelected}
                      loadingMessage={loadingMessage}
                      isVoiceMessage={!!isVoiceMessage()}
                      forceHoverStyle={isFileBlockHovered && !isVoiceMessage()}
                    />
                  )}
              </div>
            )}

            {hasText && (
              <div style={{ position: "relative" }}>
                <div
                  style={{
                    paddingRight: msg.updatedAt ? 100 : 60,
                    paddingLeft: hasFiles || hasFilesUrls ? 6 : 0,
                    paddingBottom: hasFiles || hasFilesUrls ? 3 : 0,
                  }}
                  onDoubleClick={(e) => e.stopPropagation()}
                >
                  {msg.text ? renderWithLinks(msg.text) : ""}
                </div>

                <MessageStatusIndicator
                  msg={msg}
                  hasFiles={hasFiles || hasFilesUrls}
                  isUserMsg={isUserMsg}
                  isSelected={isSelected}
                  loadingMessage={loadingMessage}
                />
              </div>
            )}

            {msg.err && (
              <Dropdown
                drop="up"
                style={{
                  position: "absolute",
                  bottom: !isVoiceMessage()
                    ? hasFiles || hasFilesUrls
                      ? 9
                      : 11
                    : 5,
                  right: !!isVoiceMessage()
                    ? 7
                    : (msg.text?.length || 0) < 1
                      ? 4
                      : isUserMsg
                        ? hasFiles || hasFilesUrls
                          ? 6
                          : 12
                        : 6,
                  zIndex: 1201,
                }}
              >
                <Dropdown.Toggle
                  as="div"
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "red",
                    cursor: "pointer",
                    zIndex: 1201,
                  }}
                  title={`Не доставлено. Нажмите, чтобы выбрать действие.\n${new Date(
                    msg.createdAt
                  ).toLocaleString("ru-RU", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })}`}
                >
                  <ExclamationCircle size={4} color="white" />
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => onResend()}>
                    Отправить снова
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => onDeleteMessage([msg._id])}>
                    Удалить сообщение
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>
          {modeSelected && (
            <Button
              className="d-flex align-items-center justify-content-center p-0 mb-1"
              style={{ width: 21, height: 21 }}
              variant={isSelected ? "secondary" : "outline-secondary"}
            >
              <Check />
            </Button>
          )}
        </div>

        <MediaModal
          isOpen={isOpen}
          media={modalMedia}
          currentIndex={currentIndex}
          onClose={closeMediaModal}
          onPrev={handlePrev}
          onNext={handleNext}
          goToMessage={(msgId) => {
            goToMessage(msgId)
            closeMediaModal()
          }}
        />
      </>
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.msg._id !== nextProps.msg._id) return false;
    if (prevProps.msg.err !== nextProps.msg.err) return false;
    if (prevProps.isSelected !== nextProps.isSelected) return false;
    if (prevProps.loadingMessage !== nextProps.loadingMessage) return false;
    if (prevProps.modeSelected !== nextProps.modeSelected) return false;

    const prevFiles = prevProps.allFiles || [];
    const nextFiles = nextProps.allFiles || [];

    if (prevFiles.length !== nextFiles.length) {
      return false;
    }

    for (let i = 0; i < prevFiles.length; i++) {
      if (prevFiles[i] !== nextFiles[i]) {
        return false;
      }
    }

    if (nextProps.isUserMsg) {
      return (
        prevProps.msg.readedAt === nextProps.msg.readedAt &&
        prevProps.msg.delivedAt === nextProps.msg.delivedAt
      );
    }

    return true;
  }
);
